// ====================================================================================
// RIGHT DRAWER - V.4 "MATH FIX, UI REFINE, & WINABILITY CONTEXT"
//
// UPDATES:
// 1. [CRITICAL FIX] - Corrected math in Actionable Insight: Now displays Persuadable 
//    Reach Index (PRI) instead of an impossible percentage.
// 2. [UI FIX] - Removed "cheap" Target icon from Strategic Pick cards.
// 3. [ENHANCEMENT] - Added a "Winability Context" panel at the bottom, pulling 
//    Risk/Opportunity Factors from the wardOverview data.
// ====================================================================================
import React from 'react';
import {
    List, Quote, Award, Calendar, BookOpen, MapPin, 
    ChevronRight, X, 
    // Extended Icon Set
    Check, Compass, Heart, PieChart, Zap, AlertTriangle, 
    Briefcase, Users, GitMerge, RadioTower, Target, 
    ArrowRight, MessageSquare, TrendingUp, BarChart3
} from 'lucide-react';

// --- Static Data for General Archetype Overview (unchanged) ---
const theiaArchetypes = {
    // Progressive Activists
    urban_progressive_core: { name: 'Urban Progressive Core', segmentKey: 'progressive_activists', description: 'Highly educated urban professionals, social justice focused', target_age: 34, target_graduate_pct: 65, location_type: 'urban', issue_priorities: { 'social_justice': 0.9, 'climate_change': 0.9, 'inequality': 0.8, 'europe': 0.8 }, channel_preferences: { 'social_media': 0.9, 'digital_ads': 0.8 }, persuadability: 0.25, activation_difficulty: 0.2 },
    climate_activist: { name: 'Climate Activist', segmentKey: 'progressive_activists', description: 'Environment-first voters, typically younger professionals', target_age: 29, target_graduate_pct: 70, location_type: 'urban', issue_priorities: { 'climate_change': 0.95, 'environment': 0.9, 'energy': 0.8 }, channel_preferences: { 'social_media': 0.95, 'events': 0.8 }, persuadability: 0.2, activation_difficulty: 0.15 },
    social_justice_warrior: { name: 'Social Justice Warrior', segmentKey: 'progressive_activists', description: 'Equality-focused activists, highly politically engaged', target_age: 31, target_graduate_pct: 68, location_type: 'urban', issue_priorities: { 'social_justice': 0.95, 'inequality': 0.9, 'discrimination': 0.85 }, channel_preferences: { 'social_media': 0.9, 'events': 0.85 }, persuadability: 0.15, activation_difficulty: 0.1 },
    // Incrementalist Left
    soft_left_liberal: { name: 'Soft-Left Liberal', segmentKey: 'incrementalist_left', description: 'Moderate left-leaning professionals, pragmatic reformers', target_age: 41, target_graduate_pct: 52, location_type: 'suburban', issue_priorities: { 'healthcare': 0.8, 'education': 0.8, 'economy': 0.7 }, channel_preferences: { 'door_to_door': 0.7, 'postal_leaflet': 0.7 }, persuadability: 0.6, activation_difficulty: 0.4 },
    civic_pragmatist: { name: 'Civic Pragmatist', segmentKey: 'incrementalist_left', description: 'Institution-trusting moderates, focus on practical solutions', target_age: 45, target_graduate_pct: 48, location_type: 'suburban', issue_priorities: { 'local_services': 0.8, 'healthcare': 0.75, 'education': 0.7 }, channel_preferences: { 'postal_leaflet': 0.8, 'door_to_door': 0.75 }, persuadability: 0.65, activation_difficulty: 0.35 },
    middle_britain_left: { name: 'Middle Britain Left', segmentKey: 'incrementalist_left', description: 'Mainstream left-of-centre voters, moderate on most issues', target_age: 48, target_graduate_pct: 35, location_type: 'mixed', issue_priorities: { 'cost_of_living': 0.8, 'healthcare': 0.75, 'economy': 0.7 }, channel_preferences: { 'postal_leaflet': 0.75, 'door_to_door': 0.7 }, persuadability: 0.7, activation_difficulty: 0.45 },
    professional_moderate: { name: 'Professional Moderate', segmentKey: 'incrementalist_left', description: 'Career-focused professionals, pragmatic left-leaning', target_age: 39, target_graduate_pct: 58, location_type: 'suburban', issue_priorities: { 'economy': 0.8, 'education': 0.75, 'healthcare': 0.7 }, channel_preferences: { 'email': 0.8, 'digital_ads': 0.7 }, persuadability: 0.55, activation_difficulty: 0.4 },
    // Established Liberals
    well_off_traditionalist: { name: 'Well-Off Traditionalist', segmentKey: 'established_liberals', description: 'Affluent, older professionals, socially liberal but economically conservative', target_age: 58, target_graduate_pct: 45, location_type: 'suburban', issue_priorities: { 'economy': 0.8, 'healthcare': 0.7, 'local_services': 0.6 }, channel_preferences: { 'postal_leaflet': 0.85, 'local_media': 0.8 }, persuadability: 0.4, activation_difficulty: 0.3 },
    established_liberal_core: { name: 'Established Liberal Core', segmentKey: 'established_liberals', description: 'Comfortable professionals, pro-system, meritocratic worldview', target_age: 54, target_graduate_pct: 55, location_type: 'metropolitan', issue_priorities: { 'economy': 0.8, 'europe': 0.7, 'education': 0.65 }, channel_preferences: { 'email': 0.8, 'postal_leaflet': 0.7 }, persuadability: 0.35, activation_difficulty: 0.25 },
    optimistic_contentment: { name: 'Optimistic Contentment', segmentKey: 'established_liberals', description: 'Satisfied older professionals, optimistic about future', target_age: 61, target_graduate_pct: 40, location_type: 'suburban', issue_priorities: { 'healthcare': 0.8, 'local_services': 0.7, 'economy': 0.6 }, channel_preferences: { 'postal_leaflet': 0.9, 'door_to_door': 0.8 }, persuadability: 0.3, activation_difficulty: 0.2 },
    // Sceptical Scrollers
    apolitical_centrist: { name: 'Apolitical Centrist', segmentKey: 'sceptical_scrollers', description: 'Disengaged from politics, distrustful of institutions', target_age: 32, target_graduate_pct: 25, location_type: 'mixed', issue_priorities: { 'cost_of_living': 0.8, 'housing': 0.7, 'jobs': 0.6 }, channel_preferences: { 'social_media': 0.6, 'digital_ads': 0.5 }, persuadability: 0.8, activation_difficulty: 0.85 },
    passive_centre: { name: 'Passive Centre', segmentKey: 'sceptical_scrollers', description: 'Low engagement, centrist views, hard to motivate', target_age: 35, target_graduate_pct: 30, location_type: 'suburban', issue_priorities: { 'cost_of_living': 0.7, 'housing': 0.6, 'local_services': 0.6 }, channel_preferences: { 'social_media': 0.5, 'digital_ads': 0.4 }, persuadability: 0.75, activation_difficulty: 0.8 },
    calm_persistence: { name: 'Calm Persistence', segmentKey: 'sceptical_scrollers', description: 'Steady, unengaged voters, consistent but low participation', target_age: 28, target_graduate_pct: 35, location_type: 'urban', issue_priorities: { 'housing': 0.8, 'cost_of_living': 0.75, 'jobs': 0.7 }, channel_preferences: { 'social_media': 0.7, 'digital_ads': 0.6 }, persuadability: 0.7, activation_difficulty: 0.75 },
    // Rooted Patriots
    left_behind_patriot: { name: 'Left-Behind Patriot', segmentKey: 'rooted_patriots', description: 'Working class, patriotic, feels economically abandoned', target_age: 58, target_graduate_pct: 12, location_type: 'post_industrial_towns', issue_priorities: { 'immigration': 0.8, 'economy': 0.75, 'jobs': 0.7 }, channel_preferences: { 'postal_leaflet': 0.85, 'door_to_door': 0.8 }, persuadability: 0.5, activation_difficulty: 0.4 },
    loyal_national: { name: 'Loyal National', segmentKey: 'rooted_patriots', description: 'Patriotic, community-focused, traditional values', target_age: 64, target_graduate_pct: 18, location_type: 'rural', issue_priorities: { 'patriotism': 0.85, 'tradition': 0.8, 'immigration': 0.75 }, channel_preferences: { 'postal_leaflet': 0.9, 'community_events': 0.8 }, persuadability: 0.4, activation_difficulty: 0.35 },
    backbone_conservative_patriot: { name: 'Backbone Conservative Patriot', segmentKey: 'rooted_patriots', description: 'Conservative-leaning patriots, reliable voters', target_age: 67, target_graduate_pct: 22, location_type: 'rural', issue_priorities: { 'tradition': 0.85, 'law_and_order': 0.8, 'immigration': 0.75 }, channel_preferences: { 'postal_leaflet': 0.9, 'local_media': 0.85 }, persuadability: 0.3, activation_difficulty: 0.25 },
    disengaged_traditionalist_patriot: { name: 'Disengaged Traditionalist Patriot', segmentKey: 'rooted_patriots', description: 'Traditional values, but politically disengaged', target_age: 55, target_graduate_pct: 15, location_type: 'rural', issue_priorities: { 'tradition': 0.7, 'economy': 0.65, 'local_services': 0.6 }, channel_preferences: { 'postal_leaflet': 0.75, 'door_to_door': 0.7 }, persuadability: 0.6, activation_difficulty: 0.7 },
    // Traditional Conservatives
    well_off_traditionalist_conservative: { name: 'Well-Off Traditionalist Conservative', segmentKey: 'traditional_conservatives', description: 'Affluent conservatives, traditional values, strong party loyalty', target_age: 68, target_graduate_pct: 35, location_type: 'rural', issue_priorities: { 'tradition': 0.9, 'law_and_order': 0.8, 'economy': 0.75 }, channel_preferences: { 'postal_leaflet': 0.95, 'local_media': 0.9 }, persuadability: 0.15, activation_difficulty: 0.1 },
    backbone_conservative_core: { name: 'Backbone Conservative Core', segmentKey: 'traditional_conservatives', description: 'Core conservative voters, highly reliable, traditional', target_age: 71, target_graduate_pct: 28, location_type: 'rural', issue_priorities: { 'tradition': 0.95, 'law_and_order': 0.9, 'immigration': 0.8 }, channel_preferences: { 'postal_leaflet': 0.95, 'door_to_door': 0.9 }, persuadability: 0.1, activation_difficulty: 0.05 },
    disengaged_traditionalist_conservative: { name: 'Disengaged Traditionalist Conservative', segmentKey: 'traditional_conservatives', description: 'Conservative-leaning but less politically active', target_age: 65, target_graduate_pct: 20, location_type: 'rural', issue_priorities: { 'tradition': 0.8, 'healthcare': 0.75, 'local_services': 0.7 }, channel_preferences: { 'postal_leaflet': 0.85, 'door_to_door': 0.75 }, persuadability: 0.25, activation_difficulty: 0.5 },
    // Dissenting Disruptors
    dissenting_disruptor_core: { name: 'Dissenting Disruptor Core', segmentKey: 'dissenting_disruptors', description: 'Anti-establishment, wants radical change, populist anger', target_age: 45, target_graduate_pct: 18, location_type: 'mixed', issue_priorities: { 'anti_establishment': 0.9, 'change': 0.85, 'immigration': 0.75 }, channel_preferences: { 'social_media': 0.8, 'rallies': 0.8 }, persuadability: 0.6, activation_difficulty: 0.45 },
    long_term_despair: { name: 'Long-Term Despair', segmentKey: 'dissenting_disruptors', description: 'Pessimistic about future, feels system has failed them', target_age: 52, target_graduate_pct: 15, location_type: 'post_industrial', issue_priorities: { 'jobs': 0.85, 'economy': 0.8, 'change': 0.8 }, channel_preferences: { 'door_to_door': 0.7, 'alternative_media': 0.65 }, persuadability: 0.7, activation_difficulty: 0.6 },
    hard_pressed_anxiety: { name: 'Hard-Pressed Anxiety', segmentKey: 'dissenting_disruptors', description: 'Economically stressed, anxious about future, volatile voters', target_age: 41, target_graduate_pct: 20, location_type: 'urban_suburban', issue_priorities: { 'cost_of_living': 0.9, 'housing': 0.85, 'jobs': 0.8 }, channel_preferences: { 'social_media': 0.7, 'door_to_door': 0.65 }, persuadability: 0.8, activation_difficulty: 0.55 },
};

export const RightDrawer = ({ isOpen, onClose, type, data }) => {
    if (!isOpen) return null;

    let title = '';
    let content = null;
    
    // --- CASE 1: RISK DETAILS (Existing) ---
    if (type === 'riskDetail' && data) {
        const colors = { political: 'text-danger-400', social: 'text-blue-400', financial: 'text-success-400', general: 'text-slate-400' };
        title = "Risk Details";
        content = (
             <div className="space-y-4">
                <div className="panel p-4 bg-slate-800/50">
                    <div className={`text-sm mb-1 font-semibold capitalize ${colors[data.category]}`}>{data.category} Risk</div>
                    <p className="text-slate-200">{data.description}</p>
                </div>
                <p className="text-xs text-slate-500">This risk was identified through semantic analysis of the document's content and tone.</p>
            </div>
        )
    }
    // --- CASE 2: POLICY THEME (Existing) ---
    else if (type === 'policyThemeDetail' && data) {
        title = data.title;
        content = (
            <div className="space-y-6">
                <div>
                    <div className="flex items-center space-x-2 mb-3 text-sm text-slate-400">
                        <List size={16} />
                        <span>Salient Concepts</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {data.details.salient_concepts.map(c => (
                            <span key={c} className="text-xs px-2.5 py-1.5 bg-slate-800 rounded border border-slate-700 text-slate-300 capitalize">{c}</span>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="flex items-center space-x-2 mb-3 text-sm text-slate-400">
                        <Quote size={16} />
                        <span>Key Commitment Statements</span>
                    </div>
                    <div className="space-y-3">
                        {data.details.commitment_statements.slice(0, 5).map(s => (
                             <p key={s} className="text-sm text-slate-300 p-3 bg-slate-800/50 border-l-2 border-accent-400">"{s}"</p>
                        ))}
                    </div>
                </div>
            </div>
        );
    } 
    // --- CASE 3: STAKEHOLDER DETAILS (Existing) ---
    else if (type === 'stakeholderDetail' && data) {
        title = `Stakeholder Focus: ${data.key.charAt(0).toUpperCase() + data.key.slice(1)}`;
        content = (
            <div className="space-y-4">
                 <div className="flex items-center space-x-2 mb-3 text-sm text-slate-400">
                    <Award size={16} />
                    <span>Example Impact Statements</span>
                </div>
                <div className="space-y-3">
                    {data.impact_statements.map(s => (
                        <p key={s} className="text-sm text-slate-300 p-3 bg-slate-800/50 border-l-2 border-accent-400">"{s}"</p>
                    ))}
                </div>
            </div>
        );
    }
    // --- CASE 4: WARD ARCHETYPE BIOGRAPHY (Existing) ---
    else if (type === 'wardArchetype' && data) {
        const { wardSpecificData, biographyData } = data;

        if (!wardSpecificData || !biographyData || !biographyData.political_biography) {
            title = "Error";
            content = (
                <div className="text-slate-400 text-sm">
                    Could not load archetype biography data. The mapping key might be incorrect or the data is missing.
                </div>
            );
        } else {
            const { political_biography, name } = biographyData;
            title = name || 'Archetype Biography'; 

            const detailItem = (Icon, title, value) => (
                <div className="bg-slate-700/30 rounded-lg p-4">
                    <div className="flex items-center mb-1">
                        <Icon size={16} className="mr-2 text-slate-400" />
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</span>
                    </div>
                    <div className="text-sm font-medium text-white">{value || 'N/A'}</div>
                </div>
            );

            const listItem = (item, index) => (
                <li key={index} className="flex items-start">
                    <Check size={14} className="mr-2 mt-1 text-accent-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                </li>
            );

            content = (
                <div className="space-y-6">
                    <div>
                        <span className={`text-sm font-semibold text-accent-400`}>
                            {political_biography.political_leaning || "Voter Archetype"}
                        </span>
                        <p className="text-sm text-white font-semibold mt-1">
                            Makes up ~{(wardSpecificData?.ward_representation_percentage || 0).toFixed(0)}% of this ward
                        </p>
                    </div>
                    
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                        <h3 className="flex items-center text-lg font-semibold text-white mb-4">
                            <BookOpen size={20} className="mr-2 text-accent-400" />
                            Political Biography
                        </h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                            {political_biography.paragraph_summary || 'No summary available.'}
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        {detailItem(Compass, "Disposition", political_biography.disposition)}
                        {detailItem(Heart, "Turnout", political_biography.turnout_reliability)}
                        {detailItem(Users, "Age Range", political_biography.probable_age_range)}
                        {detailItem(Briefcase, "Income Skew", political_biography.income_skew)}
                        {detailItem(BookOpen, "Education Skew", political_biography.education_skew)}
                        {detailItem(GitMerge, "Tactical Voting", political_biography.tactical_voting_susceptibility)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                            <h3 className="flex items-center text-lg font-semibold text-white mb-4">
                                <AlertTriangle size={20} className="mr-2 text-danger-400" />
                                Key Concerns
                            </h3>
                            <ul className="space-y-2 text-sm">
                                {political_biography.key_concerns?.length > 0 ? (
                                    political_biography.key_concerns.map(listItem)
                                ) : (
                                    <p className="text-slate-400 italic">No key concerns listed.</p>
                                )}
                            </ul>
                        </div>

                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                            <h3 className="flex items-center text-lg font-semibold text-white mb-4">
                                <Zap size={20} className="mr-2 text-success-400" />
                                Primary Mobilisers
                            </h3>
                            <ul className="space-y-2 text-sm">
                                {political_biography.primary_mobilisers?.length > 0 ? (
                                    political_biography.primary_mobilisers.map(listItem)
                                ) : (
                                    <p className="text-slate-400 italic">No primary mobilisers listed.</p>
                                )}
                            </ul>
                        </div>
                    </div>
                    
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                        <h3 className="flex items-center text-lg font-semibold text-white mb-4">
                            <PieChart size={20} className="mr-2 text-accent-400" />
                            Voting Propensity Summary
                        </h3>
                        <p className="text-sm text-slate-300 leading-relaxed">
                            {political_biography.voting_propensity_summary || 'No summary available.'}
                        </p>
                    </div>
                </div>
            );
        }
    } 

    // --- CASE 5: CHANNEL EFFICIENCY MATRIX (Fixed & Enhanced) ---
    else if (type === 'channelMatrix' && data) {
        const { archetypes = [], wardChannels = [], wardOverview } = data;
        
        title = "Channel Efficiency Matrix";

        if (!wardChannels || wardChannels.length === 0) {
            content = <p className="text-slate-400 text-sm">No channel data available.</p>;
        } else {
            
            // 1. Synthesize Data: Calculate "Persuadable Reach Index (PRI)"
            const enrichedChannels = wardChannels.map(([channelName, wardScore]) => {
                let bestArchetype = null;
                let maxReceptivity = -1;
                let persuadableReachPotential = 0; // Cumulative score (0-1.0)

                archetypes.forEach(arch => {
                    const receptivity = arch?.campaign_targeting?.channel_receptivity?.[channelName] || 0; // 0-1.0
                    const population = arch?.ward_representation_percentage || 0; // 0-100
                    const persuadability = arch?.campaign_targeting?.persuadability_score || 0; // 0-1.0

                    // CORRECTED MATH: Impact = Receptivity * (Population / 100) * Persuadability
                    // This results in a score that sums up to ~1.0 if applied to all voters (or 100 if we multiply later)
                    const raw_impact = receptivity * (population / 100) * persuadability;

                    persuadableReachPotential += raw_impact;

                    if (receptivity > maxReceptivity) {
                        maxReceptivity = receptivity;
                        bestArchetype = arch.name;
                    }
                });

                // Convert cumulative score (0-1.0) to a clear index (0-100)
                const persuadableReachIndex = persuadableReachPotential * 100;

                return {
                    name: channelName,
                    wardScore,
                    persuadableReachPotential: persuadableReachIndex, // Now the PRI (0-100)
                    bestArchetype
                };
            });

            const topStrategicChannels = [...enrichedChannels]
                .sort((a, b) => b.persuadableReachPotential - a.persuadableReachPotential)
                .slice(0, 3);

            const masterChannels = wardChannels.map(([name]) => name);
            const wardChannelsMap = new Map(wardChannels);

            const getCellColor = (value, wardValue) => {
                if (value === 'N/A' || wardValue === 'N/A') return 'text-slate-400';
                const diff = value - wardValue;
                if (diff > 10) return 'text-success-400 font-bold';
                if (diff > 0) return 'text-success-300';
                if (diff < -10) return 'text-danger-400';
                if (diff < 0) return 'text-danger-300';
                return 'text-white';
            };

            // Get data for the Actionable Insight footer
            const topChannelName = topStrategicChannels[0]?.name;
            const topChannelArchetype = archetypes.find(a => a.name === topStrategicChannels[0]?.bestArchetype);
            const maxChannelReceptivity = (topChannelArchetype?.campaign_targeting?.channel_receptivity?.[topChannelName] * 100)?.toFixed(0) || 0;
            const maxReachIndex = topStrategicChannels[0]?.persuadableReachPotential.toFixed(0) || 0; 
            
            content = (
                <div className="space-y-8">
                    {/* Header Description */}
                    <p className="text-sm text-slate-400">
                        Strategic analysis cross-referencing ward priority, archetype population size, and persuadability.
                    </p>

                    {/* STRATEGIC SYNTHESIS CARDS (Target Icon Removed) */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {topStrategicChannels.map((channel, index) => (
                            <div key={index} className="bg-slate-700/30 border border-slate-600/50 p-4 rounded-lg relative overflow-hidden">
                                {/* Subtle accent line replacing the 'cheap' icon */}
                                <div className="absolute top-0 left-0 w-1 h-full bg-accent-400"></div>

                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-accent-400 uppercase tracking-wider">
                                        #{index + 1} Strategic Pick
                                    </span>
                                    <span className="text-xs text-slate-400 flex items-center gap-1">
                                        <Users size={12} /> High Reach
                                    </span>
                                </div>
                                <h4 className="text-lg font-bold text-white capitalize mb-1">
                                    {channel.name.replace(/_/g, ' ')}
                                </h4>
                                <div className="text-xs text-slate-300 mb-3">
                                    Best for: <span className="text-white font-medium">{channel.bestArchetype}</span>
                                </div>
                                
                                {/* Contextual Recommendation */}
                                <div className="mt-2 pt-2 border-t border-slate-600/50 text-xs text-slate-400 italic">
                                    Use for {wardOverview?.recommendations?.resources?.communication_style_guide?.tone?.toLowerCase() || 'persuasive'} content.
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* DATA TABLE */}
                    <div className="overflow-x-auto bg-slate-800/50 border border-slate-700/50 rounded-xl p-2">
                        <table className="min-w-full divide-y divide-slate-700">
                            <thead className="bg-slate-800/50">
                                <tr>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Channel</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-accent-400 uppercase tracking-wider">
                                        Ward Priority
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-blue-400 uppercase tracking-wider">
                                        Est. Persuadable Reach
                                    </th>
                                    {archetypes.map((arch, i) => (
                                        <th key={i} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                            <div>{arch.name}</div>
                                            <div className="text-[10px] text-slate-500 font-normal">
                                                Pop: {arch.ward_representation_percentage.toFixed(0)}% | P: {(arch.campaign_targeting.persuadability_score*100).toFixed(0)}%
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-slate-800 divide-y divide-slate-700">
                                {masterChannels.map((channelName) => {
                                    const wardPriority = (wardChannelsMap.get(channelName) || 0) * 100;
                                    
                                    const metrics = enrichedChannels.find(c => c.name === channelName);
                                    // Visual score is now the PRI index itself (0-100)
                                    const reachScore = metrics ? metrics.persuadableReachPotential.toFixed(0) : 0; 

                                    return (
                                        <tr key={channelName} className="hover:bg-slate-700/50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white capitalize border-r border-slate-700/50">
                                                {channelName.replace(/_/g, ' ')}
                                            </td>
                                            
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-accent-400">
                                                {wardPriority.toFixed(0)}%
                                            </td>

                                            {/* Visual Bar for Persuadable Reach */}
                                            <td className="px-4 py-3 whitespace-nowrap border-r border-slate-700/50">
                                                <div className="flex flex-col justify-center h-full">
                                                    <div className="flex items-end space-x-1 mb-1">
                                                        <span className="text-xs font-mono text-blue-300">{reachScore}</span>
                                                    </div>
                                                    <div className="w-20 h-1.5 bg-slate-700 rounded-full">
                                                        <div 
                                                            className="h-1.5 bg-blue-500 rounded-full" 
                                                            style={{ width: `${Math.min(parseInt(reachScore), 100)}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Archetype Cells */}
                                            {archetypes.map((arch, i) => {
                                                const receptivity = arch?.campaign_targeting?.channel_receptivity?.[channelName];
                                                const val = receptivity !== undefined ? (receptivity * 100) : 'N/A';
                                                const wardVal = wardPriority !== 'N/A' ? wardPriority : 'N/A';
                                                const color = getCellColor(val, wardVal);
                                                return (
                                                    <td key={i} className={`px-4 py-3 whitespace-nowrap text-sm font-mono ${color} opacity-80`}>
                                                        {val !== 'N/A' ? `${val.toFixed(0)}%` : '-'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* ACTIONABLE FOOTER (Math Fixed) */}
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start space-x-3">
                        <MessageSquare className="text-blue-400 flex-shrink-0 mt-1" size={18} />
                        <div className="text-sm">
                            <span className="font-bold text-blue-400">Actionable Insight: </span>
                            <span className="text-slate-300">
                                Channels with high **Persuadable Reach Index (PRI)** scores offer the best ROI. 
                                Prioritize **{topStrategicChannels[0]?.name.replace(/_/g, ' ')}** (PRI: {maxReachIndex}) 
                                to target voters where maximum receptivity is **{maxChannelReceptivity}%**.
                            </span>
                        </div>
                    </div>

                    {/* ADVANCED SYNTHESIS ADDITION: Winability Context Panel */}
                    {wardOverview?.winability && (
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-4">
                            <h3 className="flex items-center text-lg font-semibold text-white">
                                <TrendingUp size={20} className="mr-2 text-info-400" />
                                Winability Context
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="text-xs text-danger-400 font-semibold mb-2">RISK FACTORS</div>
                                    <ul className="space-y-1 list-disc list-inside text-sm text-slate-300">
                                        {(wardOverview.winability.risk_factors || []).map((factor, i) => (
                                            <li key={i}>{factor}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="text-xs text-success-400 font-semibold mb-2">OPPORTUNITY FACTORS</div>
                                    <ul className="space-y-1 list-disc list-inside text-sm text-slate-300">
                                        {(wardOverview.winability.opportunity_factors || []).map((factor, i) => (
                                            <li key={i}>{factor}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 pt-2 border-t border-slate-700/50">
                                Current Win Probability Forecast: **{((wardOverview.winability.score || 0) * 100).toFixed(0)}%**
                            </p>
                        </div>
                    )}
                </div>
            );
        }
    }
    // --- CASE 6: OVERVIEW ARCHETYPE (General) ---
    else if (type === 'overviewArchetype' && data) {
        title = data.details.name;
        const subArchetypes = Object.values(theiaArchetypes).filter(
            archetype => archetype.segmentKey === data.key
        );

        const getTopItems = (obj) => Object.entries(obj || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

        content = (
             <div className="space-y-6">
                <div className="panel p-4 bg-slate-800/50">
                    <div className="text-sm text-slate-400 mb-1">Segment Description</div>
                    <p className="text-slate-200">{data.details.description}</p>
                </div>
                <div>
                    <div className="text-sm text-slate-400 mb-2">Sub-Archetypes in this Segment</div>
                    <div className="max-h-[60vh] overflow-y-auto space-y-3 panel p-2">
                        {subArchetypes.map((archetype, i) => (
                            <div key={i} className="p-4 bg-slate-800/80 rounded-lg border border-slate-700 space-y-4">
                                <div>
                                    <h4 className="font-semibold text-white">{archetype.name}</h4>
                                    <p className="text-xs text-slate-400 mt-1">{archetype.description}</p>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-xs text-center">
                                    <div className="bg-slate-700/50 p-2 rounded">
                                        <div className="text-slate-400 flex items-center justify-center space-x-1.5"><Calendar size={12}/><span>Target Age</span></div>
                                        <div className="text-white font-mono text-sm mt-1">{archetype.target_age}</div>
                                    </div>
                                    <div className="bg-slate-700/50 p-2 rounded">
                                        <div className="text-slate-400 flex items-center justify-center space-x-1.5"><BookOpen size={12}/><span>Graduate %</span></div>
                                        <div className="text-white font-mono text-sm mt-1">{archetype.target_graduate_pct}%</div>
                                    </div>
                                    <div className="bg-slate-700/50 p-2 rounded">
                                        <div className="text-slate-400 flex items-center justify-center space-x-1.5"><MapPin size={12}/><span>Location</span></div>
                                        <div className="text-white font-mono text-sm mt-1 capitalize">{archetype.location_type.replace(/_/g, ' ')}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-400 mb-2">Top Issues</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {getTopItems(archetype.issue_priorities).map(issue => (
                                            <span key={issue} className="text-2xs px-2 py-1 bg-slate-700 rounded-md text-slate-300">{issue}</span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-400 mb-2">Campaign Insights</div>
                                    <div className="flex space-x-4">
                                        <div>
                                            <div className="text-sm text-accent-400 font-bold">{archetype.persuadability}</div>
                                            <div className="text-2xs text-slate-400">Persuadability</div>
                                        </div>
                                         <div>
                                            <div className="text-sm text-danger-400 font-bold">{archetype.activation_difficulty}</div>
                                            <div className="text-2xs text-slate-400">Activation Difficulty</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
             </div>
        );
    }


    return (
        <aside 
            className={`fixed top-0 right-0 h-full w-[1000px] bg-slate-900 border-l border-slate-700 shadow-2xl z-50 overflow-y-auto 
                       transition-all duration-300 ease-in-out 
                       ${isOpen ? 'translate-x-0' : 'translate-x-full'} 
                       hover:drop-shadow-[0_0_10px_rgba(203,213,225,0.6)]`}
        >
            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <button onClick={onClose} className="p-2 rounded-md text-slate-400 hover:bg-slate-800 hover:text-white">
                        <X size={20} />
                    </button>
                </div>
                {content}
            </div>
        </aside>
    );
};